import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  CONNECTION_STATUS_NAME,
  DRAWER_KEY,
  FIREBASE_EVENTS,
  INTERNAL_REDIRECT_TYPE,
  MODAL_KEYS,
  NOTIFICATION_TYPE,
  PLAN_STATUS_KEY,
} from 'app-constants'
import { readMessage } from 'slices/messages'
import { setModal } from 'slices/modal'
import { setDrawer } from 'slices/drawer'
import { setServiceOrderMassive } from 'slices/serviceOrder'
import { formatDate, getFirstName, getMassiveMessage } from 'utils'
import { useFirebaseEvent, useRedirect } from 'hooks'
import { STATICS } from 'statics'
import { messagesService } from 'services'
import { Card } from 'components/card'
import { Button } from 'components/button'
import './style.scss'

export function MessagesModal() {
  const [message, setMessage] = useState({})
  const [modalOpen, setModalOpen] = useState(false)
  const [items, setItems] = useState({})
  const { handleCtaRedirect } = useRedirect(INTERNAL_REDIRECT_TYPE.MODAL, handleTypeChange)
  const { push } = useHistory()
  const { connectionStatus } = useSelector(state => state.contract)
  const { info } = useSelector(({ customer }) => customer)
  const messages = useSelector(state => state.messages)
  const { contractStatus } = useSelector(state => state.contract.selected)
  const { virtualSupportMassive } = useSelector(({ massive }) => massive)
  const { isSignalWeak, isLoading } = useSelector(({ contract }) => contract)
  const idContract = useSelector(state => state.contract.selected.id)
  const dispatch = useDispatch()
  const { sendContractWithdrawal } = messagesService()
  const { sendEvent } = useFirebaseEvent()

  useEffect(() => {
    const array = []

    if (virtualSupportMassive.message && contractStatus === PLAN_STATUS_KEY.ST_CONT_HABILITADO) {
      const massiveWarningMessage = getMassiveMessage(virtualSupportMassive)
      sendEvent(FIREBASE_EVENTS.UNITARY_WARNING_MESSAGE_MODAL)

      array.push(massiveWarningMessage)
    }

    if (messages?.contentUnread && !modalOpen) {
      messages.contentUnread.forEach(element => {
        if (element.mandatory) array.push(element)
      })
    }

    if (array.length > 0 && !modalOpen) {
      setModalOpen(true)
      setItems(array)
      setMessage({
        ...array[0],
        index: 0,
      })
    }
  }, [
    messages.contentUnread,
    modalOpen,
    idContract,
    virtualSupportMassive,
    contractStatus,
    dispatch,
  ])

  useEffect(() => {
    const { id, visualized } = message
    if (id && !visualized) {
      dispatch(readMessage(id))
    }
  }, [message, dispatch])

  function handleTypeChange() {
    dispatch(setModal({ key: MODAL_KEYS.NONE }))
  }

  function handleNextMessage() {
    handleUserInteraction()
    const index = message.index + 1
    const newMessage = {
      ...items[index],
      index,
    }
    setMessage(newMessage)
  }

  function handleCtaClick() {
    handleCtaRedirect(message.redirectScreen)
  }

  function handleCloseClick() {
    handleUserInteraction()
    push('/')

    dispatch(setModal({ key: MODAL_KEYS.NONE }))
  }

  function handleServiceOrderClick() {
    handleUserInteraction()
    dispatch(setModal({ key: MODAL_KEYS.NONE }))
    dispatch(setServiceOrderMassive(true))
    dispatch(setDrawer(DRAWER_KEY.VIRTUAL_SUPPORT))
  }

  function handleMassiveContinueClick() {
    return hasMoreMessages() ? handleNextMessage() : handleCloseClick()
  }

  function hasMoreMessages() {
    return items.length > message.index + 1
  }

  function handleUserInteraction() {
    if (message.notificationType === NOTIFICATION_TYPE.CONTACT_WITHDRAWAL) {
      sendContractWithdrawal(message.id)
    }
  }

  function isServiceOrderAvailableToSignal() {
    return connectionStatus === CONNECTION_STATUS_NAME.OPTICAL_SIGNAL
  }

  function renderMessageActions() {
    if (message.redirectScreen) {
      return (
        <Button className='button' color='primary' size='large' onClick={handleCtaClick}>
          {message.redirectLabel}
        </Button>
      )
    }

    return hasMoreMessages() ? (
      <Button className='button' color='primary' size='large' onClick={() => handleNextMessage()}>
        Ok, próximo
      </Button>
    ) : (
      <Button className='button' color='primary' size='large' onClick={handleCloseClick}>
        Ok, entendi
      </Button>
    )
  }

  function renderButtonIsSignalWeak() {
    return (
      <div className='message-massive'>
        <Button
          className='button'
          size='large'
          color='primary'
          onClick={handleServiceOrderClick}
          isLoading={isLoading}
        >
          Sim, existe
        </Button>
        {renderButtonIsEverythingIsNormal()}
      </div>
    )
  }

  function renderButtonIsEverythingIsNormal() {
    return (
      <Button
        className='button'
        size='large'
        onClick={handleMassiveContinueClick}
        isLoading={isLoading}
      >
        Está tudo normal
      </Button>
    )
  }

  function renderMassiveActions() {
    return isSignalWeak ? renderButtonIsSignalWeak() : renderButtonIsEverythingIsNormal()
  }

  function renderActions() {
    const { isMassive, isWarning } = message
    const isWarningAndHasConnectionStatus = isWarning && !isMassive && !!connectionStatus

    if (isWarningAndHasConnectionStatus)
      return isServiceOrderAvailableToSignal() ? renderMassiveActions() : renderMessageActions()

    return renderMessageActions()
  }

  return (
    <div className='message-modal'>
      <h3 className='title'>
        Olá <span className='name'>{getFirstName(info.name)}</span>, você tem novas mensagens!
      </h3>
      <div className='message-details-component-modal'>
        <Card className='card'>
          <p className='title'>{message.title}</p>
          {message.imageUrl && <img alt={message.title} src={message.imageUrl} />}
          <p className='text description'>{message.description}</p>
          <div className='box-link'>
            {message.launchUrl && (
              <a
                className='link'
                rel='noopener noreferrer'
                href={message.launchUrl}
                target='_blank'
              >
                {' '}
                {message.launchUrl}
              </a>
            )}
          </div>
          <div className='box'>
            <p className='date text'>{formatDate(message.creationDateTime)}</p>
          </div>
        </Card>
        {hasMoreMessages() && (
          <div>
            <STATICS.ARROW_RIGHT className='icon' onClick={() => handleNextMessage()} />
          </div>
        )}
      </div>
      <div className='message-actions'>{renderActions()}</div>
    </div>
  )
}
